/** Pure progression rules, used by the reward service and the character dashboard. */
import type { RewardBundle, RewardId } from '../../shared';
import type { ChapterActivity, EconomySave, TrainingStat } from '../save';

export const xpToNext = (level: number): number => 100 + (level - 1) * 45;
export const trainingCost = (rank: number): number => 30 + rank * 25;
export const TRAINING: readonly TrainingStat[] = ['might', 'grit', 'cunning'];
export function chapterPlan(s: EconomySave) {
  // A save without the new track keeps its current chapter exactly as offered.
  // After claiming, consumed thresholds retain surplus deeds without recounting
  // the previous chapter or turning an order's claim into a fresh activity.
  const focus: ChapterActivity | null = s.chapterTrack && s.chapters > 0 ? (['road', 'arena', 'cards'] as const)[(s.chapters - 1) % 3] : null;
  const goals = (['arena', 'road', 'cards'] as const).map(activity => {
    const base = s.chapterTrack?.consumed[activity] ?? s.chapters, required = activity === focus ? 2 : 1;
    const done = Math.min(required, Math.max(0, s.activities[activity] - base));
    return { activity, required, done, target: base + required };
  });
  const stat: TrainingStat | null = focus ? ({ road: 'grit', arena: 'might', cards: 'cunning' } as const)[focus] : null;
  const mastered = !!stat && s.training[stat] >= 12;
  return { focus, goals, done: goals.reduce((n, g) => n + g.done, 0), total: focus ? 4 : 3,
    gold: !focus ? 60 : mastered ? 90 : 40, stat: mastered ? null : stat, mastered };
}
export const chapterReady = (s: EconomySave): boolean => chapterPlan(s).goals.every(g => g.done >= g.required);
export function activityFor(source: string): 'arena' | 'road' | 'cards' | null {
  if (/^(arena|tourney|beast)\./.test(source)) return 'arena';
  if (/^(cards|minigame)\./.test(source)) return 'cards';
  if (/^(explore|dungeon|order)\./.test(source)) return 'road';
  return null;
}
export function rewardFor(source: RewardId, bundle: RewardBundle, level: number): { xp: number; gold: number } {
  let xp = 0, gold = 0;
  const activity = activityFor(source);
  if (activity === 'arena') {
    const won = bundle.arena ? bundle.arena.outcome !== 'loss' : source !== 'arena.loss';
    const bounty = Math.min(1.5, Math.max(0.9, bundle.arena?.bounty ?? 1));
    xp = Math.round((won ? 40 : 15) * bounty); gold = Math.round((won ? 18 : 7) * bounty);
  } else if (activity === 'cards') {
    const won = bundle.card ? bundle.card.outcome === 'win' : source.endsWith('.win') || source === 'minigame.other' || source.includes('puzzle');
    xp = won ? 30 : 12; gold = won ? 14 : 5;
    if (source === 'minigame.other') {
      const place = bundle.params?.place;
      xp = place === 1 ? 30 : place === 2 ? 18 : place === 3 ? 10 : 20;
      gold = place === 1 ? 15 : place === 2 ? 8 : place === 3 ? 4 : 10;
    }
  } else if (activity === 'road') {
    xp = 22; gold = 12;
    if (source === 'dungeon.clear') { xp = 65; gold = 35; }
    if (source === 'order.quest') { xp = 35; gold = 20; }
    // Expedition risk must matter: a safe early return earns only what was actually found.
    const steps = Math.min(8, Math.max(0, Number(bundle.params?.steps) || 0));
    const finds = Math.min(40, Math.max(0, Number(bundle.params?.finds) || 0));
    if (bundle.params?.steps !== undefined) {
      if (source === 'explore.deposit') { xp = steps * 4; gold = finds * 3; }
      if (source === 'explore.chapter') { xp = 30 + steps * 6; gold = 8 + finds * 3; }
      if (source === 'dungeon.clear') { xp = 65 + steps * 5; gold = 20 + finds * 4; }
    }
  }
  const safe = (v: unknown): number => typeof v === 'number' && Number.isFinite(v) ? Math.max(0, v) : 0;
  if (activity !== 'arena' && activity !== 'cards') {
    if (bundle.xpU !== undefined) xp = safe(bundle.xpU) * 20;
    if (bundle.xpPct !== undefined) xp += safe(bundle.xpPct) * xpToNext(level) / 100;
    if (bundle.goldG !== undefined) gold = safe(bundle.goldG);
  }
  return { xp: Math.round(xp * (1 + (level - 1) * 0.08)), gold: Math.round(gold) };
}
